import React, { createContext, useContext, useState, useCallback } from 'react';

const QuickLinksContext = createContext();

export const useQuickLinks = () => {
    const context = useContext(QuickLinksContext);
    if (!context) {
        throw new Error('useQuickLinks must be used within a QuickLinksProvider');
    }
    return context;
};

export const QuickLinksProvider = ({ children }) => {
    const [quickLinks, setQuickLinks] = useState([]);
    const [enhancedRightClickEnabled, setEnhancedRightClickEnabled] = useState(true);
    const [emailVerified, setEmailVerified] = useState(false);
    const [emailVerifiedLoading, setEmailVerifiedLoading] = useState(true); // Start as loading
    const [snapshotFrequencyMinutes, setSnapshotFrequencyMinutes] = useState(5); // Default to 5 minutes
    
    // Workspace cache - pre-fetch workspace data to reduce load times
    const [workspaceCache, setWorkspaceCache] = useState({
        workspaceDetails: null,
        pinnedDocuments: null,
        userFavorites: null,
        mostViewedDocuments: null,
        mostViewedSpaces: null,
        recentlyViewed: null,
        staleDocuments: null,
        snapshots: null,
        userNames: null,
        // Sidebar data
        spaces: null,
        pages: null,
        walkthroughs: null,
        timestamp: null // Track when data was cached
    });
    
    // Notification preferences
    const [notificationPreferences, setNotificationPreferences] = useState({
        emailNotifications: true,
        commentNotifications: true,
        documentNotifications: true,
        subscriptionNotifications: true,
        workspaceNotifications: true,
        mentionNotifications: true,
        reminderNotifications: true,
        exportNotifications: true
    });

    const updateQuickLinks = useCallback((links) => {
        setQuickLinks(links);
    }, []);

    const clearQuickLinks = useCallback(() => {
        setQuickLinks([]);
    }, []);

    const updateEnhancedRightClick = useCallback((enabled) => {
        setEnhancedRightClickEnabled(enabled);
    }, []);

    const updateEmailVerified = useCallback((verified) => {
        setEmailVerified(verified);
    }, []);

    const updateEmailVerifiedLoading = useCallback((loading) => {
        setEmailVerifiedLoading(loading);
    }, []);

    const updateNotificationPreferences = useCallback((preferences) => {
        setNotificationPreferences(preferences);
    }, []);

    const updateSnapshotFrequency = useCallback((frequencyMinutes) => {
        setSnapshotFrequencyMinutes(frequencyMinutes);
    }, []);

    const updateWorkspaceCache = useCallback((cacheData) => {
        setWorkspaceCache({
            ...cacheData,
            timestamp: Date.now()
        });
    }, []);

    const clearWorkspaceCache = useCallback(() => {
        setWorkspaceCache({
            workspaceDetails: null,
            pinnedDocuments: null,
            userFavorites: null,
            mostViewedDocuments: null,
            mostViewedSpaces: null,
            recentlyViewed: null,
            staleDocuments: null,
            snapshots: null,
            userNames: null,
            spaces: null,
            pages: null,
            walkthroughs: null,
            timestamp: null
        });
    }, []);

    return (
        <QuickLinksContext.Provider value={{ 
            quickLinks, 
            updateQuickLinks, 
            clearQuickLinks,
            enhancedRightClickEnabled,
            updateEnhancedRightClick,
            emailVerified,
            updateEmailVerified,
            emailVerifiedLoading,
            updateEmailVerifiedLoading,
            notificationPreferences,
            updateNotificationPreferences,
            snapshotFrequencyMinutes,
            updateSnapshotFrequency,
            workspaceCache,
            updateWorkspaceCache,
            clearWorkspaceCache
        }}>
            {children}
        </QuickLinksContext.Provider>
    );
};
